import React from 'react';
import { useOrganizationClientRecords } from '../../../../../../../hooks/api/useOrganizationClientRecords';
import Stack, { ContentStack } from '../../../../../../v2BaseComponents/Stack';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import { getIntl } from '../../../../../../../utils/intl';
import InfoIcon from '../../../../../../v2BaseComponents/InfoIcon';
import { PromptActions } from '../../../../../../v2BaseComponents/Prompt';

const intl = getIntl('archive-clients-prompt-content', {
    archiveClients: 'Archive Clients',
    archiveClientsPrompt: 'Are you sure you want to archive {count} clients?',
    archive: 'Archive',
    cancel: 'Cancel',
});

export type ArchiveClientsPromptContentOwnProps = {
    onClose: () => void;
    organizationClientRecordIds: string[] | undefined;
    onArchive: (organizationClientRecordIds: string[]) => void;
};

export type ArchiveClientsPromptContentProps = ArchiveClientsPromptContentOwnProps;

const Content: React.FC<{
    clientRecordIds: string[];
    onReEngageClientRecord: () => void;
    onClose: () => void;
}> = ({ clientRecordIds, onReEngageClientRecord, onClose }) => (
    <ContentStack>
        <InfoIcon
            alertIconProps={{
                name: 'Archive',
                variant: 'info',
            }}
            title={intl('archiveClients')}
            subText={intl('archiveClientsPrompt', {
                count: clientRecordIds.length,
            })}
        />
        <PromptActions
            actions={[
                {
                    title: intl('archive'),
                    onPress: onReEngageClientRecord,
                    icon: 'Archive',
                    variant: 'primary',
                    testID: 'confirm-archive-clients',
                },
                {
                    title: intl('cancel'),
                    onPress: onClose,
                    variant: 'secondary',
                    icon: 'XCircle',
                    testID: 'cancel-archive-clients',
                },
            ]}
        />
    </ContentStack>
);

const ArchiveClientsPromptContent: React.FC<ArchiveClientsPromptContentProps> = ({
    onClose,
    organizationClientRecordIds,
    onArchive,
}) => {
    const { isArchivingClientRecords } = useOrganizationClientRecords({});

    return (
        <Stack vertical fullWidth style={{ minHeight: 200 }}>
            <AnimateLoadedContent isLoading={isArchivingClientRecords || !organizationClientRecordIds}>
                {() => (
                    <Content
                        clientRecordIds={organizationClientRecordIds!}
                        onReEngageClientRecord={() => onArchive(organizationClientRecordIds!)}
                        onClose={onClose}
                    />
                )}
            </AnimateLoadedContent>
        </Stack>
    );
};

export default React.memo(ArchiveClientsPromptContent);
